
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { DateOfBirthSelector } from '@/components/ui/date-of-birth-selector';

interface BasicInfoFormProps {
  practitionerId: string;
  initialData: any;
  onUpdate: () => void;
}

export default function BasicInfoForm({ practitionerId, initialData, onUpdate }: BasicInfoFormProps) {
  const { t } = useTranslation('dashboard');
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dateOfBirth, setDateOfBirth] = useState(initialData?.date_of_birth || '');
  const [dateError, setDateError] = useState<string | null>(null);

  const handleDateChange = (date: string | null) => {
    setDateOfBirth(date || '');
    setDateError(null);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formData = new FormData(e.currentTarget);

      const updates = {
        first_name: formData.get('first_name') as string,
        last_name: formData.get('last_name') as string,
        professional_title: formData.get('professional_title') as string,
        gender: formData.get('gender') as string,
        bio: formData.get('bio') as string,
        date_of_birth: dateOfBirth || null,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('practitioners')
        .update(updates)
        .eq('id', practitionerId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Basic information updated successfully",
      });

      onUpdate();
    } catch (error) {
      console.error('Error updating basic info:', error);
      toast({
        title: "Error",
        description: "Failed to update basic information",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('basicInformation.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="first_name">{t('basicInformation.firstName')}</Label>
              <Input
                id="first_name"
                name="first_name"
                defaultValue={initialData?.first_name || ''}
                disabled={isSubmitting}
              />
            </div>
            <div>
              <Label htmlFor="last_name">{t('basicInformation.lastName')}</Label>
              <Input
                id="last_name"
                name="last_name"
                defaultValue={initialData?.last_name || ''}
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="professional_title">{t('basicInformation.professionalTitle')}</Label>
            <Input
              id="professional_title"
              name="professional_title"
              defaultValue={initialData?.professional_title || ''}
              disabled={isSubmitting}
            />
          </div>

          <div>
            <Label htmlFor="gender">{t('basicInformation.gender')}</Label>
            <Select name="gender" defaultValue={initialData?.gender || ""} disabled={isSubmitting}>
              <SelectTrigger>
                <SelectValue placeholder={t('basicInformation.selectGender')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="male">{t('genderOptions.male')}</SelectItem>
                <SelectItem value="female">{t('genderOptions.female')}</SelectItem>
                <SelectItem value="non_binary">{t('genderOptions.non_binary')}</SelectItem>
                <SelectItem value="prefer_not_to_say">{t('genderOptions.prefer_not_to_say')}</SelectItem>
                <SelectItem value="other">{t('genderOptions.other')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <DateOfBirthSelector
            value={dateOfBirth}
            onChange={handleDateChange}
            disabled={isSubmitting}
            error={dateError}
          />

          <div>
            <Label htmlFor="bio">{t('basicInformation.biography')}</Label>
            <Textarea
              id="bio"
              name="bio"
              placeholder={t('basicInformation.biographyPlaceholder')}
              defaultValue={initialData?.bio || ''}
              rows={4}
              disabled={isSubmitting}
            />
          </div>

          <Button 
            type="submit" 
            className="w-full md:w-auto bg-amber-600 hover:bg-amber-700 text-white" 
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('basicInformation.savingChanges')}
              </>
            ) : (
              t('basicInformation.saveChanges')
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
